<template>
    <app-layout v-if="isPageReady" :showTrialPeriodBar="false">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-page-header :heading="pageHeading"></common-page-header>
            </common-content-spacer>

            <referrals-new-results
                :isSidebarOpen="isMapOpen"
                :results="results"
                :criteria="$route.query"
                @changeRange="loadMatchesWithDifferentRange($event)"
            >
                <common-content-spacer v-if="results.length || physicianRecords.length" height="medium">
                    <common-grid v-if="$route.query.practiceType === PracticeTypeEnum.PHYSICIAN">
                        <common-grid-column v-for="(result, index) in results" :key="index">
                            <practice-member-card
                                :physician="result"
                                @go-to-registration="openPleaseRegisterModal()"
                            ></practice-member-card>
                        </common-grid-column>

                        <common-grid-column
                            v-if="showPhysicianRecordsTitle"
                            :key="results.length"
                            class="results--physician-records-title"
                            :width="24"
                        >
                            {{ $t('pages.referrals.new.steps.results.physicians_not_in_medmatch') }}
                        </common-grid-column>

                        <common-grid-column v-for="(result, index) in physicianRecords" :key="results.length + index + 1">
                            <practice-member-card
                                :physician="{ isRecord: true, ...result }"
                                :isUserProfileLinkEnabled="false"
                                :showNetworkInclusionStatus="false"
                                @go-to-registration="openPleaseRegisterModal()"
                            ></practice-member-card>
                        </common-grid-column>
                    </common-grid>

                    <common-grid v-if="isPracticeAncillary()">
                        <common-grid-column v-for="(result, index) in results" :key="index">
                            <ancillary-member-card :ancillary="result"></ancillary-member-card>
                        </common-grid-column>
                    </common-grid>

                    <common-buttons>
                        <common-button
                            v-if="showMoreButton"
                            :label="$t('pages.referrals.index.buttons.show_more')"
                            :isProcessing="isProcessing"
                            type="primary"
                            size="large"
                            @click="showMore()"
                        ></common-button>
                    </common-buttons>
                </common-content-spacer>

                <common-blank-slate v-else>
                    {{ $t('pages.referrals.new.steps.results.blank_slate.message') }}
                    <router-link :to="{ name: 'public.find-provider' }">
                        {{ $t('pages.referrals.new.steps.results.blank_slate.link') }}
                    </router-link>
                </common-blank-slate>
            </referrals-new-results>
        </common-content-spacer>

        <please-register-modal
            v-if="isPleaseRegisterModalOpen"
            @close="closePleaseRegisterModal"
            @go-to-registration="goToRegistration"
        ></please-register-modal>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import FacilityServicesAPI from 'api/FacilityServicesAPI'
    import PhysicianRecordsAPI from 'api/PhysicianRecordsAPI'
    import AppLayout from 'components/layouts/AppLayout'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import AncillaryMemberCard from 'components/page-elements/practice/AncillaryMemberCard'
    import PleaseRegisterModal from 'components/page-elements/public/PleaseRegisterModal'
    import GoogleAnalyticsService from 'services/GoogleAnalyticsService'

    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin],

        components: {
            AppLayout,
            PracticeMemberCard,
            AncillaryMemberCard,
            PleaseRegisterModal,
        },

        data() {
            return {
                results: [],
                physicianRecords: [],
                isMapOpen: false,
                isProcessing: false,
                offset: 0,
                totalCount: 0,
                physicianRecordsTotalCount: 0,
                PracticeTypeEnum: PracticeTypeEnum,
                isPleaseRegisterModalOpen: false,
            }
        },

        computed: {
            pageHeading() {
                const practiceType = this.$route.query.practiceType

                return this.$tc(`pages.referrals.new.steps.results.heading.${practiceType}`, this.totalCount, {
                    count: this.totalCount,
                })
            },

            showMoreButton() {
                return this.totalCount > this.results.length || this.physicianRecordsTotalCount > this.physicianRecords.length
            },

            showPhysicianRecordsTitle() {
                return this.physicianRecordsTotalCount >= this.physicianRecords.length && this.physicianRecordsTotalCount
            },
        },

        methods: {
            loadMatchesWithDifferentRange(range) {
                this.getMatches({ ...this.$route.query, range, offset: this.offset })
            },

            openPleaseRegisterModal() {
                this.isPleaseRegisterModalOpen = true
            },

            closePleaseRegisterModal() {
                this.isPleaseRegisterModalOpen = false
            },

            goToRegistration() {
                GoogleAnalyticsService.event('cta_clicked')
                this.$router.push({ name: 'auth.signup.register' })
            },

            toggleMapView(flag = !this.isMapOpen) {
                this.isMapOpen = flag
            },

            getMatches(data) {
                this.isProcessing = true
                if (this.results.length < this.totalCount) {
                    return this.isPracticeAncillary()
                        ? this.fetchAncillaryMatches({ ...this.$route.query, offset: this.offset })
                        : this.fetchMatches({ ...this.$route.query, offset: this.offset })
                }
                if (this.$route.query.practiceType === PracticeTypeEnum.PHYSICIAN) {
                    this.fetchPhysicianRecords(data)
                }
            },

            fetchMatches(data) {
                PhysiciansAPI.getMatchesList(data).then((results) => {
                    this.isProcessing = false
                    this.results = this.results.concat(results.users)
                    this.totalCount = Number(results._headers['x-total-count'])

                    if (this.results.users.length === this.totalCount) {
                        this.offset = 0
                        this.fetchPhysicianRecords({ ...data, offset: this.offset })
                    }
                })
            },

            fetchPhysicianRecords(data) {
                return PhysicianRecordsAPI.getList(data).then((results) => {
                    this.physicianRecords = this.physicianRecords.concat(results)
                    this.physicianRecordsTotalCount = Number(results._headers['x-total-count'])
                    this.isProcessing = false
                })
            },

            fetchAncillaryMatches(data) {
                data.testIds = data.testIds.toString()
                if (this.isPracticeAncillary()) {
                    data.testIds = data.testIds.toString()
                }
                FacilityServicesAPI.getMatchesList(data).then((results) => {
                    this.isProcessing = false
                    this.results = this.results.concat(results)
                    this.totalCount = Number(results._headers['x-total-count'])

                    if (this.results.length === this.totalCount) {
                        this.offset = 0
                    }
                })
            },

            showMore() {
                this.offset = Number(this.$route.query.limit) + this.offset
                this.getMatches({ ...this.$route.query, offset: this.offset })
            },

            isPracticeAncillary() {
                return this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
            },
        },

        mounted() {
            this.isPracticeAncillary()
                ? this.fetchAncillaryMatches({ ...this.$route.query, offset: this.offset })
                : this.fetchMatches({ ...this.$route.query, offset: this.offset })
        },
    }
</script>
